import { useEffect, useMemo, useRef, useState } from 'react';
import { HealthSync } from '../components/HealthSync';
import { Sheet } from '../components/Sheet';
import {
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  EQUIPMENT,
  defaultEquipmentIds,
  type EquipmentCategory,
} from '../data/equipment';
import { EXERCISES } from '../data/exercises';
import { SPLITS } from '../data/splits';
import { ACTIVITY_LABEL } from '../engine/nutrition';
import { blockInfo } from '../engine/blocks';
import { dayShort, todayIso } from '../lib/dates';
import {
  nextOccurrence,
  notificationSupport,
  requestPermission,
  scheduleNextReminder,
} from '../lib/reminders';
import { useApp } from '../store/AppStore';
import { clearState, downloadBackup, importState } from '../store/storage';
import { GOAL_LABEL, type ActivityLevel, type Goal, type SplitMode } from '../store/types';

export function Settings() {
  const { state, setProfile, setSettings } = useApp();
  const [showEquipment, setShowEquipment] = useState(false);
  const [showData, setShowData] = useState(false);

  const block = useMemo(() => blockInfo(state.settings), [state.settings]);
  const usable = useMemo(() => {
    const available = new Set(state.settings.equipment);
    return EXERCISES.filter((e) => e.equipment.every((eq) => available.has(eq))).length;
  }, [state.settings.equipment]);

  const toggleDay = (list: 'liftDays' | 'cardioDays', day: number) => {
    const current = state.profile[list];
    const other = list === 'liftDays' ? 'cardioDays' : 'liftDays';
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort();
    setProfile({
      [list]: next,
      // A day can't be both — turning one on turns the other off.
      [other]: state.profile[other].filter((d) => d !== day),
    });
  };

  return (
    <div className="stack">
      {/* ---------------------------------------------------------- you --- */}
      <div className="card">
        <div className="card-title">You</div>
        <div className="stack">
          <div className="grid-2">
            <div className="field">
              <label htmlFor="s-name">Name</label>
              <input
                id="s-name"
                value={state.profile.name}
                onChange={(e) => setProfile({ name: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="s-age">Age</label>
              <input
                id="s-age"
                type="number"
                inputMode="numeric"
                value={state.profile.age}
                onChange={(e) => setProfile({ age: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="field">
              <label htmlFor="s-height">Height (cm)</label>
              <input
                id="s-height"
                type="number"
                inputMode="numeric"
                value={state.profile.heightCm}
                onChange={(e) => setProfile({ heightCm: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="field">
              <label htmlFor="s-weight">Weight (kg)</label>
              <input
                id="s-weight"
                type="number"
                inputMode="decimal"
                step="0.1"
                value={state.profile.bodyweightKg}
                onChange={(e) => setProfile({ bodyweightKg: Number(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="s-goal">Goal</label>
            <select
              id="s-goal"
              value={state.profile.goal}
              onChange={(e) => setProfile({ goal: e.target.value as Goal })}
            >
              {(Object.keys(GOAL_LABEL) as Goal[]).map((g) => (
                <option key={g} value={g}>
                  {GOAL_LABEL[g]}
                </option>
              ))}
            </select>
            <div className="hint">
              Changes your calorie targets, which cardio gets suggested, and whether a
              conditioning finisher gets added to lifting days.
            </div>
          </div>

          <div className="field">
            <label htmlFor="s-activity">Daily activity outside the gym</label>
            <select
              id="s-activity"
              value={state.profile.activityLevel}
              onChange={(e) => setProfile({ activityLevel: e.target.value as ActivityLevel })}
            >
              {(Object.keys(ACTIVITY_LABEL) as ActivityLevel[]).map((a) => (
                <option key={a} value={a}>
                  {ACTIVITY_LABEL[a]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------ schedule --- */}
      <div className="card">
        <div className="card-title">Schedule</div>
        <div className="stack">
          <div className="field">
            <label>Lifting days</label>
            <div className="daypicker">
              {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                <button
                  key={d}
                  className={state.profile.liftDays.includes(d) ? 'on' : ''}
                  onClick={() => toggleDay('liftDays', d)}
                >
                  {dayShort(d)}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label>Cardio days</label>
            <div className="daypicker">
              {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                <button
                  key={d}
                  className={state.profile.cardioDays.includes(d) ? 'cardio-on' : ''}
                  onClick={() => toggleDay('cardioDays', d)}
                >
                  {dayShort(d)}
                </button>
              ))}
            </div>
            <div className="hint">
              A day can only be one or the other. Missing a scheduled day is fine —
              the planner works off what you've actually trained, not the calendar,
              so nothing gets skipped permanently.
            </div>
          </div>

          <div className="field">
            <label htmlFor="s-time">Usual gym time</label>
            <input
              id="s-time"
              type="time"
              value={state.profile.gymTime}
              onChange={(e) => setProfile({ gymTime: e.target.value })}
            />
          </div>
        </div>
      </div>

      <Reminders />

      <HealthSync />

      {/* ------------------------------------------------------- program --- */}
      <div className="card">
        <div className="card-title">Program</div>
        <div className="stack">
          <div className="field">
            <label htmlFor="s-split">Split</label>
            <select
              id="s-split"
              value={state.settings.splitMode}
              onChange={(e) => setSettings({ splitMode: e.target.value as SplitMode })}
            >
              <option value="auto">Auto — alternate every block</option>
              {SPLITS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} only
                </option>
              ))}
            </select>
          </div>

          <div className="note small">
            <strong>{block.split.name}</strong> — {block.split.rationale}
          </div>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="s-blocklen">Block length (weeks)</label>
              <input
                id="s-blocklen"
                type="number"
                inputMode="numeric"
                min="2"
                max="12"
                value={state.settings.blockLengthWeeks}
                onChange={(e) =>
                  setSettings({ blockLengthWeeks: Math.max(2, Number(e.target.value) || 4) })
                }
              />
            </div>
            <div className="field">
              <label htmlFor="s-deload">Deload every N blocks</label>
              <input
                id="s-deload"
                type="number"
                inputMode="numeric"
                min="0"
                max="6"
                value={state.settings.deloadEveryNBlocks}
                onChange={(e) =>
                  setSettings({ deloadEveryNBlocks: Math.max(0, Number(e.target.value) || 0) })
                }
              />
              <div className="hint">0 = never</div>
            </div>
          </div>

          <div className="field">
            <label htmlFor="s-start">Program start date</label>
            <input
              id="s-start"
              type="date"
              value={state.settings.programStartDate}
              max={todayIso()}
              onChange={(e) => setSettings({ programStartDate: e.target.value })}
            />
            <div className="hint">
              Everything about blocks and deloads is counted from here. Currently in
              block {block.blockNumber}, week {block.weekInBlock}.
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------- equipment --- */}
      <div className="card">
        <div className="row between">
          <div className="grow">
            <div className="card-title" style={{ margin: 0 }}>
              Equipment
            </div>
            <div className="small muted" style={{ marginTop: 4 }}>
              {state.settings.equipment.length} of {EQUIPMENT.length} on · {usable} exercises
              available
            </div>
          </div>
          <button className="btn sm" onClick={() => setShowEquipment(true)}>
            Edit
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------- display --- */}
      <div className="card">
        <div className="card-title">Display</div>
        <div className="toggle-row">
          <div className="grow">
            <div>Exercise illustrations</div>
            <div className="tiny faint">Show the generated start/finish frames.</div>
          </div>
          <input
            type="checkbox"
            checked={state.settings.showImages}
            onChange={(e) => setSettings({ showImages: e.target.checked })}
            aria-label="Show exercise illustrations"
          />
        </div>

        <div className="toggle-row">
          <div className="grow">
            <div>Supersets</div>
            <div className="tiny faint">
              Pair accessories that don't compete — curls with pushdowns, leg
              extensions with leg curls — and rest once after the pair. Cuts a
              good chunk off the session. Turn off for full rest on everything.
            </div>
          </div>
          <input
            type="checkbox"
            checked={state.settings.useSupersets}
            onChange={(e) => setSettings({ useSupersets: e.target.checked })}
            aria-label="Use supersets"
          />
        </div>
      </div>

      {/* ---------------------------------------------------------- data --- */}
      <div className="card">
        <div className="row between">
          <div className="grow">
            <div className="card-title" style={{ margin: 0 }}>
              Your data
            </div>
            <div className="small muted" style={{ marginTop: 4 }}>
              {state.workouts.length} workouts · {state.cardio.length} cardio ·{' '}
              {state.metrics.length} measurements
            </div>
          </div>
          <button className="btn sm" onClick={() => setShowData(true)}>
            Backup
          </button>
        </div>
      </div>

      <div className="tiny faint center" style={{ padding: '4px 0 8px' }}>
        Iron Ledger · everything stays on this device · not medical advice
      </div>

      <EquipmentSheet open={showEquipment} onClose={() => setShowEquipment(false)} />
      <DataSheet open={showData} onClose={() => setShowData(false)} />
    </div>
  );
}

function Reminders() {
  const { state, setProfile } = useApp();
  const [permission, setPermission] = useState(notificationSupport());
  const [next, setNext] = useState<Date | null>(null);

  const { remindersEnabled, reminderLeadMinutes, liftDays, cardioDays, gymTime } = state.profile;

  useEffect(() => {
    const days = [...liftDays, ...cardioDays];
    const target = scheduleNextReminder({
      enabled: remindersEnabled,
      days,
      time: gymTime,
      leadMinutes: reminderLeadMinutes,
      title: 'Gym in an hour',
      body: 'Iron Ledger has your session ready.',
    });
    setNext(target ?? nextOccurrence(days, gymTime, reminderLeadMinutes));
  }, [remindersEnabled, reminderLeadMinutes, liftDays, cardioDays, gymTime, permission]);

  const enable = async () => {
    const result = await requestPermission();
    setPermission(result);
    setProfile({ remindersEnabled: result === 'granted' });
  };

  return (
    <div className="card">
      <div className="card-title">Reminders</div>

      <div className="toggle-row">
        <div className="grow">
          <div>Nudge me before the gym</div>
          <div className="tiny faint">
            {next
              ? `Next: ${next.toLocaleString('en-GB', {
                  weekday: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : 'No upcoming session scheduled.'}
          </div>
        </div>
        {permission === 'granted' ? (
          <input
            type="checkbox"
            checked={state.profile.remindersEnabled}
            onChange={(e) => setProfile({ remindersEnabled: e.target.checked })}
            aria-label="Enable reminders"
          />
        ) : (
          <button className="btn sm" onClick={enable} disabled={permission === 'unsupported'}>
            {permission === 'denied' ? 'Blocked' : 'Allow'}
          </button>
        )}
      </div>

      <div className="field" style={{ marginTop: 10 }}>
        <label htmlFor="s-lead">Minutes before</label>
        <input
          id="s-lead"
          type="number"
          inputMode="numeric"
          min="0"
          max="240"
          value={state.profile.reminderLeadMinutes}
          onChange={(e) => setProfile({ reminderLeadMinutes: Number(e.target.value) || 0 })}
        />
      </div>

      <div className="warning" style={{ marginTop: 11 }}>
        <span>
          Worth knowing: a web app can only fire a reminder while it has a tab alive,
          so this works if you open the app at some point that day and not otherwise.
          {permission === 'denied' && ' Notifications are currently blocked in your browser settings.'}{' '}
          For something you can rely on, set a repeating alarm on your phone as well.
        </span>
      </div>
    </div>
  );
}

function EquipmentSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { state, toggleEquipment, setEquipment } = useApp();
  const selected = new Set(state.settings.equipment);

  const setCategory = (category: EquipmentCategory, on: boolean) => {
    const ids = EQUIPMENT.filter((e) => e.category === category).map((e) => e.id);
    const next = new Set(selected);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setEquipment([...next]);
  };

  return (
    <Sheet open={open} title="Equipment" onClose={onClose}>
      <p className="small muted">
        Seeded for MyFitness Galerija Centrs — mostly Technogym, with a Precor Booty
        Zone and a recovery area. It's a starting guess, not a stock list. Walk the
        floor once and switch off whatever isn't there; anything off here never gets
        recommended.
      </p>

      <div className="row" style={{ gap: 7, margin: '12px 0' }}>
        <button className="btn ghost sm" onClick={() => setEquipment(defaultEquipmentIds())}>
          Reset to defaults
        </button>
        <button className="btn ghost sm" onClick={() => setEquipment([])}>
          Clear all
        </button>
      </div>

      <div className="stack">
        {CATEGORY_ORDER.map((category) => {
          const items = EQUIPMENT.filter((e) => e.category === category);
          const onCount = items.filter((e) => selected.has(e.id)).length;
          return (
            <div key={category}>
              <div className="row between" style={{ marginBottom: 4 }}>
                <span className="section-title">{CATEGORY_LABEL[category]}</span>
                <button
                  className="btn ghost sm"
                  onClick={() => setCategory(category, onCount < items.length)}
                >
                  {onCount < items.length ? 'All' : 'None'}
                </button>
              </div>
              {items.map((item) => (
                <div className="toggle-row" key={item.id}>
                  <div className="grow">
                    <div className="small">{item.name}</div>
                    {item.note && <div className="tiny faint">{item.note}</div>}
                  </div>
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggleEquipment(item.id)}
                    aria-label={item.name}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}

function DataSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { state, replaceState } = useApp();
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);

  const onFile = async (file: File) => {
    try {
      const next = importState(await file.text());
      replaceState(next);
      setMessage('Backup restored.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not read that file.');
    }
  };

  return (
    <Sheet open={open} title="Your data" onClose={onClose}>
      <p className="small muted">
        Everything lives in this browser's storage on this device. Nothing is sent
        anywhere. That also means clearing your browser data wipes it — so take a
        backup now and then, especially before switching phones.
      </p>

      <div className="stack" style={{ marginTop: 14 }}>
        <button className="btn primary block" onClick={() => downloadBackup(state)}>
          Download backup (.json)
        </button>

        <button className="btn block" onClick={() => fileInput.current?.click()}>
          Restore from backup
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
            e.target.value = '';
          }}
        />

        {message && <div className="note small">{message}</div>}

        <div className="divider" />

        <button
          className="btn danger block"
          onClick={() => {
            if (
              confirm(
                'Erase everything — workouts, cardio, measurements, settings? This cannot be undone.',
              )
            ) {
              clearState();
              location.reload();
            }
          }}
        >
          Erase all data
        </button>
      </div>
    </Sheet>
  );
}
